import { useEffect, useRef, useState } from 'react';
import { Button, Spinner } from '@vwt/ui';
import { useTranslation } from '../lib/i18n.js';

/** The slice of a TanStack `useInfiniteQuery` result the sentinel needs. */
export interface InfiniteListQuery {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isFetchNextPageError: boolean;
  fetchNextPage: () => unknown;
}

/** Start loading the next page this far before the end of the list scrolls into view. */
const PREFETCH_MARGIN_PX = 400;

/** Nearest ancestor that scrolls vertically; `null` means the viewport. */
export function scrollParent(el: HTMLElement): HTMLElement | null {
  for (let node = el.parentElement; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') return node;
  }
  return null;
}

/**
 * Placed after the last row of a paginated list: loads the next page when the
 * end of the list comes within PREFETCH_MARGIN_PX of the visible area, so the
 * list grows as the user scrolls instead of needing a "Load more" click.
 *
 * It keeps loading while the end stays visible (a first page shorter than the
 * viewport fills it), never fires twice for the same page, and stops on a
 * failed page load, offering an explicit retry instead of a request loop.
 */
export function InfiniteScrollSentinel({ query }: { query: InfiniteListQuery }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [atEnd, setAtEnd] = useState(false);
  const { hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage } = query;

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => setAtEnd(entries.some((entry) => entry.isIntersecting)),
      { root: scrollParent(el), rootMargin: `0px 0px ${PREFETCH_MARGIN_PX}px 0px` },
    );
    observer.observe(el);
    return () => {
      observer.disconnect();
      setAtEnd(false);
    };
  }, [hasNextPage]);

  useEffect(() => {
    if (atEnd && hasNextPage && !isFetchingNextPage && !isFetchNextPageError) void fetchNextPage();
  }, [atEnd, hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage]);

  if (!hasNextPage) return null;
  return (
    <div ref={ref} className="flex min-h-12 items-center justify-center gap-2 p-3 text-sm text-fg-muted" aria-live="polite">
      {isFetchNextPageError ? (
        <Button variant="secondary" size="sm" onClick={() => void fetchNextPage()}>
          {t('common.retry')}
        </Button>
      ) : isFetchingNextPage ? (
        <>
          <Spinner /> {t('common.loading')}
        </>
      ) : null}
    </div>
  );
}
